import React, { useState } from 'react';
import { X, Link2, Copy, Check, Globe, Lock, ExternalLink } from 'lucide-react';
import { shareReport, unshareReport } from '../../services/reportBuilderService';

export default function ShareReportModal({ isOpen, onClose, templateId, templateName, shareId: initialShareId, isPublic: initialIsPublic }) {
  const [shareId, setShareId] = useState(initialShareId || null);
  const [isPublic, setIsPublic] = useState(initialIsPublic || false);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const shareUrl = shareId ? window.location.origin + '/report/view/' + shareId : '';

  const handleShare = async () => {
    setLoading(true);
    try {
      const data = await shareReport(templateId);
      setShareId(data.shareId);
      setIsPublic(true);
    } catch (err) {
      console.error('Failed to share:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleUnshare = async () => {
    setLoading(true);
    try {
      await unshareReport(templateId);
      setIsPublic(false);
    } catch (err) {
      console.error('Failed to unshare:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
        <div className="flex items-center justify-between p-5 border-b">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <Link2 className="w-5 h-5 text-indigo-500" /> Share Report
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-5 space-y-4">
          <p className="text-sm text-gray-600">Share "<strong>{templateName}</strong>" with anyone via a public link. No login required to view.</p>

          {shareId && isPublic ? (
            <>
              <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                <Globe className="w-5 h-5 text-green-600 flex-shrink-0" />
                <span className="text-sm text-green-700 font-medium">Public link is active</span>
              </div>

              <div className="flex gap-2">
                <input type="text" readOnly value={shareUrl} className="flex-1 bg-gray-50 border rounded-lg px-3 py-2 text-sm text-gray-700 select-all" onClick={e => e.target.select()} />
                <button onClick={handleCopy} className="px-3 py-2 bg-indigo-600 text-white rounded-lg text-sm hover:bg-indigo-700 flex items-center gap-1">
                  {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  {copied ? 'Copied' : 'Copy'}
                </button>
              </div>

              <a href={shareUrl} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm text-indigo-600 hover:text-indigo-700">
                <ExternalLink className="w-4 h-4" /> Open in new tab
              </a>

              <button onClick={handleUnshare} disabled={loading} className="w-full flex items-center justify-center gap-2 px-4 py-2 border border-red-200 text-red-600 rounded-lg text-sm hover:bg-red-50 disabled:opacity-50">
                <Lock className="w-4 h-4" /> {loading ? 'Disabling...' : 'Disable Public Link'}
              </button>
            </>
          ) : (
            <>
              <div className="flex items-center gap-2 p-3 bg-gray-50 border rounded-lg">
                <Lock className="w-5 h-5 text-gray-400" />
                <span className="text-sm text-gray-600">This report is currently private</span>
              </div>

              <button onClick={handleShare} disabled={loading} className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 disabled:opacity-50">
                <Globe className="w-4 h-4" /> {loading ? 'Generating...' : 'Generate Public Link'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
